import { Component, HostListener, OnDestroy, OnInit, VERSION } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { ConfigService } from '@services/config/config.service';
import { StorageService } from '@services/storage/storage.service';
import { get } from 'lodash-es';

import { environment } from '../environments/environment';
import { SessionModalComponent } from '../app/shared/modals/session-modal/session-modal.component';

// imported RA blocks
import '@wdpr/button';
import '@wdpr/modal';
import '@wdpr/pocket-panel';
import '@wdpr/toast';
import '@wdpr/input';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  /* istanbul ignore next */
  private readonly userIdTokenKey = environment.production ? 'user-id-token' : 'user-id-token-dev';

  propsToShow = ['userAgent', 'os', 'browser', 'device', 'os_version', 'browser_version', 'deviceType', 'orientation'];
  deviceInfo = null;
  version = VERSION.full;
  userAgentInputVal = null;
  ua: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  hasAccess = false;
  hasLoaded = false;
  config: any;
  title = 'Cast Measurements';

  timeInMins = 0;
  timeInSecs = 0;
  count = 1;
  interval: any;
  initialTime: any;

  authService$: Subscription;

  @HostListener('document:keydown', ['$event'])
  @HostListener('click', ['$event'])
  @HostListener('wheel', ['$event'])
  @HostListener('mousedown', ['$event'])

  resetTimer() {
    this.setTime();
  }

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private storageService: StorageService,
    private router: Router,
    private modalService: NgbModal,
    public translate: TranslateService
  ) {
  }

  ngOnInit() {
    this.configApp();
    this.setTime();
    this.startTimer();
  }

  setTime() {
    this.initialTime = new Date();
    this.timeInMins = environment.sessionTimeOut;
    this.timeInSecs = this.timeInMins * 60;
    this.count = 1;
  }

  startTimer() {
    this.interval = setInterval(() => {
      this.updateTimes();
    }, 1000);
  }

  updateTimes() {
    const baseTime = Date.now();
    const timer = (50 * 60) - Math.abs((this.initialTime - baseTime) / 1000);

    // need re-sync time if workstation is dormant
    if (timer < this.timeInSecs) {
      this.timeInSecs = Math.round(timer);
      this.timeInMins = Math.round(this.timeInSecs / 60);
      this.count = 0;
    }

    if (this.timeInSecs > 0) {
      this.count += 1;

      /* istanbul ignore if */
      if (this.count === 59) {
        this.timeInMins = this.timeInMins - 1;
        this.count = 0;
      }
    } else {
      clearInterval(this.interval);
      /* istanbul ignore next */
      const opts = {
        centered: true,
        windowClass: 'modal-content-drawer small-fixed-width',
        keyboard: false,
        backdrop: 'static' as const
      };

      // remove session
      this.authService.logout(false);
      this.storageService.setSession('user', null);
      this.storageService.setSession(this.userIdTokenKey, null);
      this.storageService.setSession('favorites', null);
      // open session timeout modal
      const modalRef = this.modalService.open(SessionModalComponent, opts);

      /* istanbul ignore next */
      modalRef.componentInstance.close = () => {
        window.scrollTo(0, 0);
        modalRef.close();
      };
    }
    this.timeInSecs = this.timeInSecs - 1;
  }

  continueWorking() {
    this.setTime();
    this.authService$ = this.authService.validateToken()
      .subscribe(res => {
        return res;
      },
        /* istanbul ignore next */
        error => {
          if (get(error, 'originalError.status') === 401) {
            this.hasAccess = false;
            this.authService.login();
          }
        });
  }

  signOut() {
    this.authService.logout(true);
  }

  /* istanbul ignore next */
  configApp() {
    try {
      this.ua = window.navigator.userAgent;
      console.log(this.ua);
    } catch {
      console.log('UA not available');
    }
    try {
      console.log('calling config service from app component');
      this.configService.load().then(_result => {
        this.authService$ = this.authService.validateToken()
          .subscribe(res => {
            return res;
          },
            /* istanbul ignore next */
            error => {
              if (get(error, 'originalError.status') === 401) {
                this.hasAccess = false;
                this.authService.login();
              }
              if (get(error, 'originalError.status') === 403) {
                this.router.navigateByUrl(`/access-error/403`);
              }
            });
      }).catch(() => {
        this.authService$ = this.authService.validateToken().subscribe(res => {
          this.hasAccess = true;
          this.hasLoaded = true;

          return res;
        }, error => {
          if (get(error, 'originalError.status') === 401) {
            this.hasAccess = false;
            this.hasLoaded = true;
            this.authService.login();
          }
          if (get(error, 'originalError.status') === 403) {
            this.router.navigateByUrl(`/access-error/403`);
          }
        });
      });
    } catch {
      console.log('Login failed');
    }
  }

  isLoggedIn() {
    return this.authService.isLoggedIn();
  }

  ngOnDestroy(): void {
    this.authService$.unsubscribe();
  }
}
